/**
 * Local listing categories.
 *
 * These take effect as a fallback when the hosted listing-categories asset
 * (`appCdnAssets.categories`) is empty or missing — see `mergeConfig` in
 * `src/util/configHelpers.js`. Pushing content to the hosted asset still
 * overrides this file, so an admin can swap in alternative categories from
 * Console without a code deploy.
 *
 * Each entry follows Sharetribe's expected shape:
 *   { name, id, subcategories: [...] }
 *
 * Extra fields:
 *   - `hidden: true` — preserved through validation. The navigation menu
 *     skips entries with this flag, but the id remains valid for listings,
 *     deep links and search filters. Use this to soft-launch a category.
 *   - `iconName: 'foo'` — preserved through validation. Looked up by
 *     `getCategoryIcon` in `src/components/CategoryMenu/icons/` to
 *     render the inline-SVG icon component next to the Level 1 label.
 *     Unknown / unset names just render the row without an icon.
 *   - `viewAllQuantifier: 'alt'` — preserved through validation. Overrides
 *     the Norwegian quantifier in the "Se alle {category}" link for
 *     neuter/mass nouns (e.g. "Se alt Sport & fritid"). Defaults to "alle".
 *   - `note` — documentation-only, stripped by `validateCategoryConfig`
 *     in `src/util/configHelpers.js` before exposing via
 *     `useConfiguration().categoryConfiguration`.
 *
 * CommonJS, so the server can read it too: the sitemap lists a page for every
 * visible category (server/resources/sitemap.js). The app imports it as usual.
 */
/**
 * How "&" in a category name is written: the one switch for the whole site
 * (Guro, 14 October; she hasn't chosen yet).
 *   null  as now: "&" in the menu, footer and admin emails ("Bluser & skjorter"),
 *         "og" in category page titles and descriptions ("bluser og skjorter")
 *   'og'  "og" everywhere
 *   '&'   "&" everywhere
 * Hand-written title wording (SearchPage.categoryPhrase.<id>, e.g. "sports- og
 * fritidsklær") is left as written.
 */
const CATEGORY_AND = null;

/** Which word stands for "&" in names and in titles, for a switch setting. */
const categoryAndForms = setting => ({
  names: setting === 'og' ? 'og' : '&',
  titles: setting === '&' ? '&' : 'og',
});

/** `text` with every "&" written as `form` ('og' or '&'). */
const writeAnd = (text, form) => text.replace(/\s*&\s*/g, ` ${form} `);

const AND_FORMS = categoryAndForms(CATEGORY_AND);

const categoryTree = [
  {
    name: 'Klær',
    id: 'klar',
    iconName: 'clothing',
    note: 'Hovedinngang for alle plagg',
    subcategories: [
      { name: 'Bluser & skjorter', id: 'klar-bluser', subcategories: [] },
      { name: 'T-skjorter & topper', id: 'klar-tskjorter', subcategories: [] },
      { name: 'Gensere', id: 'klar-gensere', subcategories: [] },
      { name: 'Cardigans', id: 'klar-cardigans', subcategories: [] },
      {
        name: 'Kjoler',
        id: 'klar-kjoler',
        subcategories: [
          { name: 'Minikjoler', id: 'klar-kjoler-mini', subcategories: [] },
          { name: 'Midikjoler', id: 'klar-kjoler-midi', subcategories: [] },
          { name: 'Maxikjoler', id: 'klar-kjoler-maxi', subcategories: [] },
        ],
      },
      { name: 'Bukser', id: 'klar-bukser', subcategories: [] },
      { name: 'Jeans', id: 'klar-jeans', subcategories: [] },
      { name: 'Shorts', id: 'klar-shorts', subcategories: [] },
      { name: 'Jumpsuits', id: 'klar-jumpsuits', subcategories: [] },
      { name: 'Skjørt', id: 'klar-skjort', subcategories: [] },
      {
        name: 'Jakker',
        id: 'klar-jakker',
        subcategories: [
          { name: 'Blazere', id: 'klar-jakker-blazere', subcategories: [] },
          { name: 'Skinnjakker', id: 'klar-jakker-skinn', subcategories: [] },
          { name: 'Dunjakker', id: 'klar-jakker-dun', subcategories: [] },
          { name: 'Trenchcoats', id: 'klar-jakker-trench', subcategories: [] },
          { name: 'Kåper', id: 'klar-jakker-kaper', subcategories: [] },
          { name: 'Regnjakker', id: 'klar-jakker-regn', subcategories: [] },
          { name: 'Denimjakker', id: 'klar-jakker-denim', subcategories: [] },
        ],
      },
      {
        name: 'Sport & fritid',
        id: 'klar-sport',
        viewAllQuantifier: 'alt',
        subcategories: [
          { name: 'Bukser', id: 'klar-sport-bukser', subcategories: [] },
          { name: 'Topper', id: 'klar-sport-topper', subcategories: [] },
          { name: 'Jakker', id: 'klar-sport-jakker', subcategories: [] },
          { name: 'Gensere', id: 'klar-sport-gensere', subcategories: [] },
          // Hidden (Guro, 12 October): no listings yet.
          { name: 'Sportstilbehør', id: 'klar-sport-tilbehor', hidden: true, subcategories: [] },
        ],
      },
      // Entry point removed (client, Phase 2). `hidden: true` drops it from
      // the nav, the CategoryMenu and the landing-page chips, while keeping
      // the id valid for the listings already tagged with it, their deep
      // links and search filters — so nothing is orphaned.
      { name: 'Loungewear', id: 'klar-loungewear', hidden: true, subcategories: [] },
    ],
  },
  {
    name: 'Sko',
    id: 'sko',
    iconName: 'shoe',
    note: 'Hovedinngang for sko',
    subcategories: [
      { name: 'Sneakers', id: 'sko-sneakers', subcategories: [] },
      { name: 'Boots & støvletter', id: 'sko-boots', subcategories: [] },
      { name: 'Pensko', id: 'sko-pensko', subcategories: [] },
      { name: 'Flate sko', id: 'sko-flate', subcategories: [] },
      { name: 'Sandaler', id: 'sko-sandaler', subcategories: [] },
      { name: 'Sport & utendørs', id: 'sko-sport', viewAllQuantifier: 'alt', subcategories: [] },
      // Hidden (Guro, 22 October): no listings yet. Remove `hidden` to reopen.
      { name: 'Tøfler & innesko', id: 'sko-tofler', hidden: true, subcategories: [] },
    ],
  },
  {
    name: 'Tilbehør',
    id: 'acc',
    iconName: 'bag',
    note: 'Hovedinngang for tilbehør',
    subcategories: [
      { name: 'Vesker', id: 'acc-vesker', subcategories: [] },
      {
        name: 'Smykker',
        id: 'acc-smykker',
        subcategories: [
          { name: 'Halskjeder', id: 'acc-smykker-hals', subcategories: [] },
          // Hidden (Guro, 22 October): no listings yet. Remove `hidden` to reopen.
          { name: 'Øredobber', id: 'acc-smykker-ore', hidden: true, subcategories: [] },
          { name: 'Ringer', id: 'acc-smykker-ring', subcategories: [] },
          { name: 'Armbånd', id: 'acc-smykker-arm', subcategories: [] },
          { name: 'Anheng & charms', id: 'acc-smykker-anheng', hidden: true, subcategories: [] },
          { name: 'Brosjer', id: 'acc-smykker-brosjer', hidden: true, subcategories: [] },
          { name: 'Klokker', id: 'acc-smykker-klokker', hidden: true, subcategories: [] },
        ],
      },
      { name: 'Belter', id: 'acc-belter', subcategories: [] },
      { name: 'Skjerf & sjal', id: 'acc-skjerf', subcategories: [] },
      { name: 'Luer, capser & hatter', id: 'acc-hatter', subcategories: [] },
      { name: 'Briller', id: 'acc-briller', subcategories: [] },
      { name: 'Hansker & votter', id: 'acc-hansker', subcategories: [] },
      { name: 'Hårtilbehør', id: 'acc-har', subcategories: [] },
      { name: 'Diverse tilbehør', id: 'acc-diverse', subcategories: [] },
    ],
  },
  {
    name: 'Bunad',
    id: 'bunad',
    hidden: true,
    note: 'Hovedkategori — skjult fra start. Bygges ut når volum tilsier det.',
    subcategories: [],
  },
];

// The names as the switch above writes them.
const withAndForm = list =>
  list.map(category => ({
    ...category,
    name: writeAnd(category.name, AND_FORMS.names),
    subcategories: withAndForm(category.subcategories || []),
  }));

const categories = withAndForm(categoryTree);

module.exports = { categories, AND_FORMS, categoryAndForms, writeAnd };
